import { fromJS } from 'immutable'
import { useRef } from 'react'

import {
  addUUID,
  emptyContactForm,
  emptyGoogleMaps,
  emptyIcon,
  emptyImage,
  emptyProduct,
  emptyText,
  emptyVideo,
} from '../../../editorSidebarBlockTemplates'
import { useTranslate } from '../../../../utils/translate'
import useEventHandler from '../../../../utils/hooks/useEventHandler'

export const contentElements = {
  text: {
    key: 'text',
    icon: ({ children }) => <div className="dali-grid-element-selection-text">{children}</div>,
    generate: () => fromJS(addUUID(emptyText)),
  },
  image: {
    key: 'image',
    icon: ({ children }) => <div className="dali-grid-element-selection-image">{children}</div>,
    generate: () => fromJS(addUUID(emptyImage)),
  },
  product: {
    key: 'product',
    icon: ({ children }) => <div className="dali-grid-element-selection-product">{children}</div>,
    generate: () => fromJS(addUUID(emptyProduct)),
  },
  video: {
    key: 'video',
    icon: ({ children }) => <div className="dali-grid-element-selection-video">{children}</div>,
    generate: () => fromJS(addUUID(emptyVideo)),
  },
  contactform: {
    key: 'contactForm',
    icon: ({ children }) => <div className="dali-grid-element-selection-contact">{children}</div>,
    generate: () => fromJS(addUUID(emptyContactForm)),
  },
  gmaps: {
    key: 'gmaps',
    icon: ({ children }) => <div className="dali-grid-element-selection-maps">{children}</div>,
    generate: () => fromJS(addUUID(emptyGoogleMaps)),
  },
  icon: {
    key: 'icon',
    icon: ({ children }) => <div className="dali-grid-element-selection-icon">{children}</div>,
    generate: () => fromJS(addUUID(emptyIcon)),
  },
}

const contentElementsForContext = {
  'nested-headline-block': [contentElements.gmaps, contentElements.text, contentElements.image, contentElements.video],
  contactform: [contentElements.gmaps, contentElements.text, contentElements.image, contentElements.video],
  gmaps: [contentElements.contactform, contentElements.text, contentElements.image, contentElements.video],
  default: [contentElements.text, contentElements.image, contentElements.product, contentElements.video],
}

export function ElementInsertionMenu({
  context,
  onNewElement,
  onCancel,
}: Readonly<{
  context: keyof typeof contentElementsForContext
  onNewElement: (element: ImmutableMap) => void
  onCancel: () => void
}>) {
  const t = useTranslate('interface', 'components.elementInsertionComponent')
  const ref = useRef<HTMLDivElement | null>(null)

  useEventHandler('mouseup', (event) => {
    setTimeout(() => {
      // When the outside click is on another menu opener button inside the
      // same row, then in this deferred check the menu is already closed
      // because the component was unmounted (`ref.current === null`). In this
      // case, `onCancel` should not be called or otherwise it will close the
      // just opened menu. For all other cases: Close the menu when clicking
      // outside of it.
      if (ref.current && !ref.current.contains(event.target)) {
        onCancel()
      }
    }, 0)
  })

  return (
    <div ref={ref}>
      <p className="dali-grid-element-selection-title" id="dali-grid-element-selection-title">
        {t('instruction')}
      </p>
      <div className="dali-grid-element-selection" role="group" aria-describedby="dali-grid-element-selection-title">
        {contentElementsForContext[context].map((contentElement) => {
          return (
            <button key={contentElement.key} onClick={() => onNewElement(contentElement.generate())}>
              <contentElement.icon>{t(contentElement.key)}</contentElement.icon>
            </button>
          )
        })}
        <button className="dali-grid-element-selection-cancel" onClick={() => onCancel()}>
          {t('cancelButton.label')}
        </button>
      </div>
    </div>
  )
}
